import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { createBrandListFromProp, updateBrandListFromProp, getViewModelBulkUpsertErrors, extractAndRestoreFailedBrandItemAssociations } from '@bingads-webui-campaign-react/brand-lists-data';
import { Panel, PanelType, PrimaryButton, DefaultButton } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { BrandsContextProvider } from '../brands-picker/brands-data';
import { ManageBrandListPanelContent } from './manage-brand-list-panel-content';
import { RequestBrandPanel } from './request-brand-panel';
import { SaveDialog } from './save-dialog';
import { CancelDialog } from './cancel-dialog';
import { BRAND_OPTIONS_DISPLAY_LIMIT, BRAND_SELECTED_OPTIONS_LIMIT, DO_NOT_ASK_AGAIN_PREFERENCE_NAME } from '../constants';

const getStyles = (_components, { palette }) => ({
  ManageBrandListPanel: {
    '& .ms-Panel-main': {
      overflow: 'hidden',
    },
    '& .ms-Panel-footer': {
      background: palette.white,
    },
  },
  SaveButton: {
    marginRight: 8,
  },
  Error: {
    color: palette.redDark,
  },
});

export const ManageBrandListPanel = ({
  className, cid, aid, brandListId, isOpen, onDismiss, onSaveCallback, isConfirmDialogOn, selectedOptionsLimit, optionsDisplayLimit,
}) => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  const [isSaveDialogOpen, { setTrue: onOpenSaveDialog, setFalse: onDismissSaveDialog }] = useBoolean(false);
  const [isCancelDialogOpen, { setTrue: onOpenCancelDialog, setFalse: onDismissCancelDialog }] = useBoolean(false);

  const [isManagePanelOpen, setIsManagePanelOpen] = useState(false);
  const [isRequestBrandPanelOpen, setIsRequestBrandPanelOpen] = useState(false);

  const onOpenRequestBrandPanel = useCallback(() => {
    setIsManagePanelOpen(false);
    setIsRequestBrandPanelOpen(true);
  }, []);
  const onDismissRequestBrandPanel = useCallback(() => {
    setIsManagePanelOpen(true);
    setIsRequestBrandPanelOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsManagePanelOpen(true);
    }
  }, [isOpen]);

  const preferenceService = useContext(PreferenceContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isDoNotAskAgain = useMemo(() => preferenceService.findByNameAtAccountLevel(DO_NOT_ASK_AGAIN_PREFERENCE_NAME) || false, [preferenceService, isOpen]);
  const setIsDoNotAskAgain = value => preferenceService.setAtAccountLevel({
    Name: DO_NOT_ASK_AGAIN_PREFERENCE_NAME,
    Data: value,
  });

  const [listName, setListName] = useState('');
  const [deletedBrandIds, setDeletedBrandIds] = useState([]);
  const [addedBrandIds, setAddedBrandIds] = useState([]);
  const [savedDeletedBrandIds, setSavedDeletedBrandIds] = useState([]);
  const [savedAddedBrandIds, setSavedAddedBrandIds] = useState([]);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setListName('');
      setServerError(null);
      setDeletedBrandIds([]);
      setAddedBrandIds([]);
      setSavedDeletedBrandIds([]);
      setSavedAddedBrandIds([]);
    }
  }, [brandListId, isOpen]);

  // This callback tracks user-made changes to the brand lists selection.
  const onChange = useCallback((newSelectedBrands, [newDeletedBrandIds, newAddedBrandIds]) => {
    setDeletedBrandIds(newDeletedBrandIds);
    setAddedBrandIds(newAddedBrandIds);
  }, []);

  const onError = useCallback((result, brandList, brandItems) => {
    const { errors } = result;
    const {
      savedAddedBrandItemIds: newSavedAddedBrandIds,
      savedDeletedBrandItemIds: newSavedDeletedBrandIds,
      filteredErrors,
    } = extractAndRestoreFailedBrandItemAssociations(errors, brandList, brandItems);

    setSavedAddedBrandIds(newSavedAddedBrandIds);
    setSavedDeletedBrandIds(newSavedDeletedBrandIds);

    const translatedErrors = getViewModelBulkUpsertErrors(i18n, {
      ...result,
      errors: filteredErrors,
    });
    const finalErrorMsg = _.chain(translatedErrors)
      .map(error => error.message)
      .uniq(true)
      .join('\n')
      .value();
    setServerError(finalErrorMsg);
  }, [i18n]);

  const onCreate = useCallback(() => {
    const brandList = {
      Id: -1,
      Name: listName,
    };
    const brandItems = _.map(addedBrandIds, brandId => ({
      Id: brandId,
      Status: 'Active',
      ParentId: -1,
    }));
    brandList.BrandItems = brandItems;

    const brandListPayload = {
      BrandLists: [brandList],
    };

    const saveCallPromise = createBrandListFromProp({ cid, aid });
    saveCallPromise(brandListPayload)
      .then((result) => {
        if (onSaveCallback) {
          onSaveCallback({
            listId: result.BrandLists[0].Id,
            listName,
          });
        }
        onDismiss();
      })
      .catch((result) => {
        onError(result, brandList, brandItems);
      });
  }, [addedBrandIds, aid, cid, listName, onDismiss, onError, onSaveCallback]);

  const onUpdate = useCallback(() => {
    const activeBrands = _.map(addedBrandIds, brandId => ({
      Id: brandId,
      Status: 'Active',
    }));
    const deleteBrands = _.map(deletedBrandIds, brandId => ({
      Id: brandId,
      Status: 'Deleted',
    }));
    const brandItems = _.map([...activeBrands, ...deleteBrands], brand => ({
      Id: brand.Id,
      Status: brand.Status,
      ParentId: brandListId,
    }));

    const brandListPayload = {
      BrandItems: brandItems,
    };

    const saveCallPromise = updateBrandListFromProp({ cid, aid });
    saveCallPromise(brandListPayload)
      .then(() => {
        if (onSaveCallback) {
          onSaveCallback();
        }
        onDismiss();
      })
      .catch((result) => {
        onError(result, null, brandItems);
      });
  }, [addedBrandIds, deletedBrandIds, cid, aid, brandListId, onSaveCallback, onDismiss, onError]);

  const onSave = useCallback(() => {
    if (brandListId) {
      onUpdate();
    } else {
      onCreate();
    }
  }, [brandListId, onCreate, onUpdate]);

  const onRenderFooterContent = useCallback(() => {
    const isBrandsChanged = !_.isEmpty(addedBrandIds) || !_.isEmpty(deletedBrandIds);
    const isChanged = brandListId ? isBrandsChanged : listName;
    return (
      <div>
        <PrimaryButton className={classNames(classes.SaveButton, 'save-button')} disabled={!isChanged} onClick={isConfirmDialogOn && !isDoNotAskAgain ? onOpenSaveDialog : onSave}>
          {i18n.getString(_TL_('Save'))}
        </PrimaryButton>
        <DefaultButton className={classNames(classes.Cancel, 'cancel-button')} onClick={isConfirmDialogOn && isChanged ? onOpenCancelDialog : onDismiss}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </div>
    );
  }, [addedBrandIds, brandListId, classes.Cancel, classes.SaveButton, deletedBrandIds, i18n, isConfirmDialogOn, isDoNotAskAgain, listName, onDismiss, onOpenCancelDialog, onOpenSaveDialog, onSave]);

  return isOpen && (
    <BrandsContextProvider
      cid={cid}
      aid={aid}
      brandListId={brandListId}
      selectedOptionsLimit={selectedOptionsLimit}
      optionsDisplayLimit={optionsDisplayLimit}
      onChange={onChange}
      savedAddedSelectedOptionsKeys={savedAddedBrandIds}
      savedDeletedSelectedOptionsKeys={savedDeletedBrandIds}
    >
      {isManagePanelOpen && (
        <Panel
          className={classNames(className, classes.ManageBrandListPanel, 'manage-brand-list-panel', brandListId ? 'edit' : 'create')}
          headerText={brandListId ? i18n.getString(_TL_('Add brand')) : i18n.getString(_TL_('Create a brand list'))}
          closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
          type={PanelType.medium}
          isOpen={isManagePanelOpen}
          onDismiss={onDismiss}
          onRenderFooterContent={onRenderFooterContent}
          isFooterAtBottom
          // this is to work around a bug in Fluent UI where the panel will close when closing the dialog, https://github.com/microsoft/fluentui/issues/6476
          onOuterClick={_.noop}
        >
          <ManageBrandListPanelContent
            brandListId={brandListId}
            name={listName}
            onNameChange={setListName}
            onOpenRequestBrandPanel={onOpenRequestBrandPanel}
            isConfirmDialogOn={isConfirmDialogOn}
          />
          {serverError && <div className={classNames(classes.Error, 'server-error')}>{serverError}</div>}
        </Panel>
      )}
      {isRequestBrandPanelOpen && (
        <RequestBrandPanel
          cid={cid}
          aid={aid}
          isOpen={isRequestBrandPanelOpen}
          onDismiss={onDismissRequestBrandPanel}
        />
      )}
      {isSaveDialogOpen && (
        <SaveDialog
          open={isSaveDialogOpen}
          onConfirm={({ isDoNotAskAgain: value }) => {
            if (value) {
              setIsDoNotAskAgain(true);
            }
            onDismissSaveDialog();
            onSave();
          }}
          onDismiss={onDismissSaveDialog}
        />
      )}
      {isCancelDialogOpen && (
        <CancelDialog
          open={isCancelDialogOpen}
          onConfirm={() => {
            onDismissCancelDialog();
            onDismiss();
          }}
          onDismiss={onDismissCancelDialog}
        />
      )}
    </BrandsContextProvider>
  );
};

ManageBrandListPanel.propTypes = {
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  brandListId: PropTypes.number,
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onSaveCallback: PropTypes.func,
  isConfirmDialogOn: PropTypes.bool,
  selectedOptionsLimit: PropTypes.number,
  optionsDisplayLimit: PropTypes.number,
};

ManageBrandListPanel.defaultProps = {
  brandListId: undefined,
  className: undefined,
  onSaveCallback: undefined,
  isConfirmDialogOn: false,
  selectedOptionsLimit: BRAND_SELECTED_OPTIONS_LIMIT,
  optionsDisplayLimit: BRAND_OPTIONS_DISPLAY_LIMIT,
};
